import React from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';

import type { DeviceInfo } from 'js/src/components/app/recorder/types';
import { DeviceTypes } from 'js/src/components/app/recorder/types';
import { BUTTON_COLORS, BUTTON_WIDTHS } from 'js/src/components/common/ui/Button';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { Dropdown, DROPDOWN_DIVIDER, DROPDOWN_HEADER } from 'js/src/components/common/ui/Dropdown';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';

const toggleProps = Immutable.fromJS({
    iconLeft: 'settings',
    className: 'device-settings-toggle d-inline-block',
    width: BUTTON_WIDTHS.NO_MIN_WIDTH,
    color: BUTTON_COLORS.SIMPLE,
});

const convertDeviceInputsToDropdownItems = (
    deviceType: DeviceTypes,
    deviceInputs: DeviceInfo[],
    activeDeviceId?: string,
    disabled = false,
) => {
    const items: DropdownItemProp<SelectDataType>[] = [];
    deviceInputs.forEach((deviceInput) => {
        const item: DropdownItemProp<SelectDataType> = ImmutableFromJS({
            id: deviceInput.id,
            text: deviceInput.label,
            className: classNames({
                'fw-bold': deviceInput.id === activeDeviceId,
            }),
            onSelectData: {
                deviceType,
                deviceId: deviceInput.id,
            },
            disabled,
        });
        items.push(ImmutableFromJS(item));
    });
    return items;
};

type SelectDataType = {
    deviceType: DeviceTypes;
    deviceId: string;
};
type ImmutableSelectDataType = ImmutableMap<SelectDataType>;

type Props = {
    availableMicrophoneDevices: DeviceInfo[];
    availableCameraDevices: DeviceInfo[];
    availableScreenDevices: DeviceInfo[];
    activeMicrophoneDeviceId: string | undefined;
    activeCameraDeviceId: string | undefined;
    activeScreenDeviceId: string | undefined;
    onDeviceChange: (deviceType: DeviceTypes, deviceId: string) => void;
};

const DeviceSelectorButton: React.FC<Props> = ({
    availableMicrophoneDevices,
    availableCameraDevices,
    availableScreenDevices,
    activeMicrophoneDeviceId,
    activeCameraDeviceId,
    activeScreenDeviceId,
    onDeviceChange,
}) => {
    const onSelect = React.useCallback(
        (selectedOption: ImmutableSelectDataType) => {
            onDeviceChange(selectedOption.get('deviceType'), selectedOption.get('deviceId'));
        },
        [onDeviceChange],
    );

    const dropdownItems = React.useMemo(() => {
        const items: DropdownItemProp<SelectDataType>[] = [];

        if (availableMicrophoneDevices.length > 0) {
            items.push(
                ImmutableFromJS({
                    id: DROPDOWN_HEADER,
                    text: 'Mic',
                    icon: 'mic',
                    className: 'd-flex align-items-center',
                }),
            );
            const audioDeviceItems = convertDeviceInputsToDropdownItems(
                DeviceTypes.AUDIO,
                availableMicrophoneDevices,
                activeMicrophoneDeviceId,
            );
            items.push(...audioDeviceItems);
            items.push(ImmutableFromJS({ id: DROPDOWN_DIVIDER }));
        }

        if (availableCameraDevices.length > 0) {
            items.push(
                ImmutableFromJS({
                    id: DROPDOWN_HEADER,
                    text: 'Camera',
                    icon: 'videocam',
                    className: 'd-flex align-items-center',
                }),
            );
            const videoDeviceItems = convertDeviceInputsToDropdownItems(
                DeviceTypes.VIDEO,
                availableCameraDevices,
                activeCameraDeviceId,
            );
            items.push(...videoDeviceItems);
            items.push(ImmutableFromJS({ id: DROPDOWN_DIVIDER }));
        }

        if (availableScreenDevices.length > 0) {
            items.push(
                ImmutableFromJS({
                    id: DROPDOWN_HEADER,
                    text: 'Screen recording',
                    icon: 'screen_record',
                    className: 'd-flex align-items-center',
                }),
            );
            const screenDeviceItems = convertDeviceInputsToDropdownItems(
                DeviceTypes.SCREEN,
                availableScreenDevices,
                activeScreenDeviceId,
            );
            items.push(...screenDeviceItems);
        }

        return Immutable.List<DropdownItemProp<SelectDataType>>(items);
    }, [
        activeCameraDeviceId,
        activeMicrophoneDeviceId,
        activeScreenDeviceId,
        availableCameraDevices,
        availableMicrophoneDevices,
        availableScreenDevices,
    ]);

    return (
        <Dropdown
            toggleProps={toggleProps}
            itemPropsList={dropdownItems}
            onSelect={onSelect}
            breakOverflow
        />
    );
};
export default React.memo(DeviceSelectorButton);
